import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { SegSelect, SegSelectOption } from "../../../SegUI/SegSelect/SegSelect";
import { SegInput } from "../../../SegUI/SegInput/SegInput";
import { SegPatente } from "../../../SegUI/SegPatente/SegPatente";
import { SegTooltip } from "../../../SegUI/SegTooltip/SegTooltip";
import { EmpresaProps } from "../../../../Services/Providers/useConfiguracionEmpresa";
import {
  obtenerMarcas,
  obtenerModelos,
  obtenerAnios,
  obtenerVersiones,
  obtenerAniosPorVersion,
} from "../../../../Services/API/bff/auto";
import { CampoCotizacion, ValoresCotizacion } from "./validaciones";
import { conAsterisco } from "../../../SegUI/SegRequerido/conAsterisco";

export type ModoVehiculo = "patente" | "manual";
export type RubroVehiculo = "auto" | "moto";

interface BloqueVehiculoProps {
  rubro: RubroVehiculo;
  config: EmpresaProps;
  isUY: boolean;
  /** "patente": el vehículo lo trajo el datacar y solo falta el año. */
  modo: ModoVehiculo;
  /**
   * Fuerza marca, modelo y versión como texto libre. Se usa cuando el datacar
   * encontró el vehículo pero el catálogo no lo reconoce por nombre: no hay
   * ids con los que encadenar los desplegables.
   */
  textoLibreForzado?: boolean;
  valores: ValoresCotizacion;
  patente?: string;
  errores: Partial<Record<CampoCotizacion, string>>;
  onCambio: (parcial: Partial<ValoresCotizacion>) => void;
  /** Valida un campo "al salir" (blur), para marcarlo si quedó vacío. */
  onRevisar?: (campo: CampoCotizacion) => void;
  /** Solo en modo patente: descarta el vehículo del datacar y pasa a manual. */
  onNoEsMiVehiculo?: () => void;
  /** Solo en modo manual dentro de Auto: vuelve al paso de la patente. */
  onVolverAPatente?: () => void;
  /**
   * Avisa qué años ofrece el catálogo, para que el padre descarte un año que
   * ya no esté entre las opciones (el datacar puede traer uno que no cotiza).
   */
  onOpcionesAnio?: (opciones: SegSelectOption[]) => void;
}

const Card = styled.div`
  background: #fff;
  border: 1px solid #dde3ec;
  border-radius: 10px;
  padding: 18px;
`;

const Encabezado = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const Datos = styled.div`
  flex: 1;
  min-width: 190px;
`;

const Acciones = styled.div`
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;

const Encontrado = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #edf9f3;
  color: #0f9a67;
  border: 1px solid #c9ecdb;
  border-radius: 100px;
  padding: 3px 11px;
  font-size: 12px;
  font-weight: 700;
`;

const Enlace = styled.button`
  background: none;
  border: 0;
  padding: 0;
  font-size: 14px;
  color: #4a5468;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  &:hover {
    color: #1b62d8;
  }
`;

const MensajeError = styled.span`
  font-size: 12.5px;
  color: #d1424f;
  font-weight: 500;
  min-height: 18px;
  display: block;
`;

const Etiqueta = styled.span`
  font-size: 13.5px;
  font-weight: 500;
  color: #4a5468;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

/** Asterisco de campo obligatorio, pegado al texto del label. */
const aOpciones = (items: any): SegSelectOption[] =>
  Array.isArray(items)
    ? items.map((item: any) => ({ value: item, label: item }))
    : [];

/** Años hacia atrás que ofrece la lista fija (UY moto, sin catálogo). */
const ANIOS_ATRAS = 40;

/**
 * Lista fija de años, del actual hacia atrás. Se usa donde no hay catálogo que
 * encadene el año (UY moto): la marca/modelo/versión son texto libre y el año
 * es un desplegable fijo, no una cascada como en Argentina.
 */
const aniosFijos = (): SegSelectOption[] => {
  const actual = new Date().getFullYear();
  return Array.from({ length: ANIOS_ATRAS + 1 }, (_, i) => {
    const anio = `${actual - i}`;
    return { value: anio, label: anio };
  });
};

export const BloqueVehiculo = ({
  rubro,
  config,
  isUY,
  modo,
  textoLibreForzado = false,
  valores,
  patente,
  errores,
  onCambio,
  onRevisar,
  onNoEsMiVehiculo,
  onVolverAPatente,
  onOpcionesAnio,
}: BloqueVehiculoProps) => {
  const methods = useForm();

  const [marcas, setMarcas] = useState<SegSelectOption[]>();
  const [modelos, setModelos] = useState<SegSelectOption[]>();
  const [anios, setAnios] = useState<SegSelectOption[]>();
  const [versiones, setVersiones] = useState<SegSelectOption[]>();

  const [cargandoModelos, setCargandoModelos] = useState(false);
  const [cargandoAnios, setCargandoAnios] = useState(false);
  const [cargandoVersiones, setCargandoVersiones] = useState(false);

  /**
   * Campos a mano en vez de desplegables encadenados: en UY la moto no tiene
   * catálogo, y con el flag del BFF puede pasar que el catálogo no reconozca
   * el vehículo que trajo el datacar.
   */
  const textoLibre = (isUY && rubro === "moto") || textoLibreForzado;

  const marcaValue = valores.marca?.value;
  const modeloValue = valores.modelo?.value;
  const anioValue = valores.anio?.value;

  const colorEmpresa =
    config.origen !== "SEGURARSE"
      ? { selected: config.colors.accent, hover: config.colors.tertiary }
      : undefined;
  const bordeEmpresa =
    config.origen !== "SEGURARSE" ? config.colors.accent : undefined;

  /* ── modo patente: el año sale del catálogo de la versión del datacar ── */
  useEffect(() => {
    if (modo !== "patente") return;
    // El vehículo del datacar se hidrata desde el store DESPUÉS del montaje, así
    // que esperamos a tener marca y versión para no pedir años con parámetros
    // vacíos. Van en las dependencias para re-ejecutar cuando lleguen.
    const marca = valores.marca?.value;
    const version = valores.version?.value;
    if (!marca || !version) return;
    let vigente = true;
    const fetchAnios = async () => {
      const response: any = await obtenerAniosPorVersion(
        marca,
        version,
        rubro === "moto" ? "Moto" : "Auto"
      );
      if (!vigente) return;
      const opciones = aOpciones(response);
      setAnios(opciones);
      onOpcionesAnio?.(opciones);
    };
    fetchAnios();
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, valores.marca?.value, valores.version?.value, rubro]);

  /* ── modo manual: marcas ── */
  useEffect(() => {
    if (modo !== "manual") return;
    let vigente = true;
    const fetchMarcas = async () => {
      const response: any = await obtenerMarcas(rubro);
      if (!vigente) return;
      // El catálogo trae las marcas MÁS ELEGIDAS arriba de un separador y el
      // listado completo (alfabético) abajo. Las dejamos primero + separador +
      // el resto sin duplicar. No cambia el estilo del select ni la búsqueda:
      // react-select filtra por label y el separador queda deshabilitado.
      const iSep = Array.isArray(response)
        ? response.indexOf("------------")
        : -1;
      if (iSep === -1) {
        setMarcas(aOpciones(response));
        return;
      }
      const destacadas: string[] = response.slice(0, iSep);
      const yaDestacada = new Set(destacadas);
      const resto: string[] = response
        .slice(iSep + 1)
        .filter((m: string) => !yaDestacada.has(m));
      setMarcas([
        ...aOpciones(destacadas),
        { value: "separator", label: "──────────" },
        ...aOpciones(resto),
      ]);
    };
    fetchMarcas();
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, rubro]);

  /* ── modo manual: modelos según marca ── */
  useEffect(() => {
    // El early return va antes de tocar el estado: en modo patente los años
    // los administra el efecto de arriba y estos no deben pisarlos.
    if (modo !== "manual") return;
    if (textoLibre || !marcaValue) {
      setModelos(undefined);
      return;
    }
    let vigente = true;
    setCargandoModelos(true);
    const fetchModelos = async () => {
      const response: any = await obtenerModelos(valores.marca, rubro);
      if (!vigente) return;
      setModelos(aOpciones(response));
      setCargandoModelos(false);
    };
    fetchModelos();
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, textoLibre, marcaValue, rubro]);

  /* ── modo manual: años según marca y modelo ── */
  useEffect(() => {
    if (modo !== "manual") return;
    // UY moto: sin catálogo, no se busca el año por marca/modelo. Es una lista
    // fija (a diferencia de Argentina, que encadena los desplegables).
    if (textoLibre) {
      setAnios(aniosFijos());
      return;
    }
    if (!marcaValue || !modeloValue) {
      setAnios(undefined);
      return;
    }
    let vigente = true;
    setCargandoAnios(true);
    const fetchAniosManual = async () => {
      const response: any = await obtenerAnios(
        valores.marca,
        valores.modelo,
        rubro
      );
      if (!vigente) return;
      setAnios(aOpciones(response));
      setCargandoAnios(false);
    };
    fetchAniosManual();
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, textoLibre, marcaValue, modeloValue, rubro]);

  /* ── modo manual: versiones según marca, modelo y año ── */
  useEffect(() => {
    if (modo !== "manual") return;
    if (textoLibre || !marcaValue || !modeloValue || !anioValue) {
      setVersiones(undefined);
      return;
    }
    let vigente = true;
    setCargandoVersiones(true);
    const fetchVersiones = async () => {
      const response: any = await obtenerVersiones(
        valores.marca,
        valores.modelo,
        valores.anio,
        rubro
      );
      if (!vigente) return;
      setVersiones(aOpciones(response));
      setCargandoVersiones(false);
    };
    fetchVersiones();
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, textoLibre, marcaValue, modeloValue, anioValue, rubro]);

  const select = (
    campo: CampoCotizacion,
    etiqueta: string,
    opciones: SegSelectOption[] | undefined,
    seleccionado: SegSelectOption | undefined,
    onSelect: (opcion: SegSelectOption) => void,
    placeholderDeshabilitado: string,
    cargando = false
  ) => (
    <div className="col-12 col-md-6" data-campo={campo}>
      <Etiqueta>{conAsterisco(etiqueta)}</Etiqueta>
      <SegSelect
        name={campo}
        label=""
        options={opciones || []}
        selected={seleccionado || null}
        setSelected={onSelect}
        width={"100%"}
        height={"md"}
        openDefault={false}
        loading={cargando}
        disabled={!opciones}
        placeholder={opciones ? "Seleccioná" : placeholderDeshabilitado}
        borderColor={errores[campo] ? "#d1424f" : undefined}
        hoverBorderColor={bordeEmpresa}
        focusBorderColor={bordeEmpresa}
        empresaColor={colorEmpresa}
      />
      <MensajeError>{errores[campo]}</MensajeError>
    </div>
  );

  const inputLibre = (
    campo: "marca" | "modelo" | "version",
    etiqueta: string,
    tooltip?: string
  ) => (
    <div className="col-12 col-md-6" data-campo={campo}>
      <Etiqueta>
        {conAsterisco(etiqueta)}
        {tooltip && <SegTooltip text={tooltip} />}
      </Etiqueta>
      <SegInput
        label=""
        name={campo}
        type="text"
        width={100}
        placeHolder="Escribí aquí"
        value={valores[campo]?.value || ""}
        onChange={(e) => {
          const texto = e.target.value;
          onCambio({ [campo]: { label: texto, value: texto } });
        }}
        onBlur={() => onRevisar?.(campo)}
        onKeyDown={(event) => {
          if (event.key === " " && !valores[campo]?.value) {
            event.preventDefault();
          }
        }}
        leyendaError={errores[campo]}
        borderColor={
          errores[campo] ? "var(--bs-form-invalid-border-color)" : bordeEmpresa
        }
        focusBorderColor={
          errores[campo]
            ? "var(--bs-form-invalid-border-color)"
            : bordeEmpresa || "#2c7dfd"
        }
      />
    </div>
  );

  const nombreVehiculo = rubro === "moto" ? "moto" : "auto";

  return (
    <Card>
      <FormProvider {...methods}>
        {modo === "patente" ? (
          <>
            <Encabezado>
              {patente && (
                <div className="flex-shrink-0">
                  <SegPatente text={patente} />
                </div>
              )}
              <Datos>
                <div className="fs-20 fw-medium">{valores.marca?.label}</div>
                <div className="fs-16">{valores.version?.label}</div>
              </Datos>
              <Acciones>
                <Encontrado>Encontrado</Encontrado>
                {onNoEsMiVehiculo && (
                  <Enlace type="button" onClick={onNoEsMiVehiculo}>
                    No es mi {nombreVehiculo}
                  </Enlace>
                )}
              </Acciones>
            </Encabezado>
            <div className="row mt-3">
              {select(
                "anio",
                "Año del modelo",
                anios,
                valores.anio,
                (opcion) => onCambio({ anio: opcion }),
                "Cargando años…",
                !anios
              )}
            </div>
          </>
        ) : (
          <>
            <div className="row">
              {textoLibreForzado
                ? inputLibre("marca", "Marca")
                : select(
                    "marca",
                    "Marca",
                    marcas,
                    valores.marca,
                    (opcion) =>
                      onCambio({
                        marca: opcion,
                        modelo: undefined,
                        anio: undefined,
                        version: undefined,
                      }),
                    "Cargando marcas…",
                    !marcas
                  )}

              {textoLibre
                ? inputLibre("modelo", "Modelo")
                : select(
                    "modelo",
                    "Modelo",
                    modelos,
                    valores.modelo,
                    (opcion) =>
                      onCambio({
                        modelo: opcion,
                        anio: undefined,
                        version: undefined,
                      }),
                    "Elegí la marca primero",
                    cargandoModelos
                  )}

              {select(
                "anio",
                "Año del modelo",
                anios,
                valores.anio,
                (opcion) => onCambio({ anio: opcion, version: undefined }),
                textoLibre ? "Ingresá el modelo primero" : "Elegí el modelo primero",
                cargandoAnios
              )}

              {textoLibre
                ? inputLibre("version", "Versión", "Encontrala en la cédula verde.")
                : select(
                    "version",
                    "Versión",
                    versiones,
                    valores.version,
                    (opcion) => onCambio({ version: opcion }),
                    "Elegí el año primero",
                    cargandoVersiones
                  )}
            </div>
            {onVolverAPatente && (
              <Enlace
                type="button"
                className="mt-2"
                onClick={onVolverAPatente}
              >
                Probar de nuevo con la patente
              </Enlace>
            )}
          </>
        )}
      </FormProvider>
    </Card>
  );
};
